import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { FaArrowLeftLong } from "react-icons/fa6";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Swal from "sweetalert2";

import Button from "../../../components/common/Button/Button";
import ReusableMuiTable from "../../../components/common/Table/Table";
import FormInput from "../../../components/common/FormInput/FormInput";
import { showToast } from "../../../components/common/ToastNotification/ToastNotification";

import {
  getCategoryTypeSpecifications,
  addCategoryTypeSpecification,
  updateCategoryTypeSpecification,
  getCategorySpecificationsById,
  deleteCategorySpecification,
} from "../../../api/service/products.service";

const ROWS_PER_PAGE = 10;

const CategorySpecification = () => {
  const { id } = useParams();

  const [specifications, setSpecifications] = useState([]);
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);

  const [formValue, setFormValue] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");

  
  const [searchInput, setSearchInput] = useState(""); 
  const [searchSpecification, setSearchSpecification] = useState(""); 

  const debounceRef = useRef(null);

  const fetchSpecifications = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);

      const res = await getCategoryTypeSpecifications(
        id,
        page,
        ROWS_PER_PAGE,
        searchSpecification
      );

      if (res?.success) {
        setSpecifications(res.data.data || []);
        setTotalRecords(res.data.total || 0);
      }
    } catch {
      showToast.error("Failed to load specifications");
    } finally {
      setLoading(false);
    }
  }, [id, page, searchSpecification]);


  useEffect(() => {
    fetchSpecifications();
  }, [fetchSpecifications]);


  const handleSearchChange = (text) => {
    setSearchInput(text || "");

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      setPage(1);
      setSearchSpecification(text || "");
    }, 500);
  };


  const handleEdit = async (row) => {
    try {
      setLoading(true);
      const res = await getCategorySpecificationsById(row.id);
      if (res?.success) {
        setFormValue(res.data.text);
        setEditingId(row.id);
      }
    } catch {
      showToast.error("Failed to load category specification");
    } finally {
      setLoading(false);
    }
  };


  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This category specification will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d32f2f",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it",
    });

    if (!result.isConfirmed) return;

    try {
      setLoading(true);
      const res = await deleteCategorySpecification(row.id);
      if (res?.success) {
        showToast.success("Deleted successfully");
        fetchSpecifications();
      }
    } catch {
      showToast.error("Delete failed");
    } finally {
      setLoading(false);
    }
  };

  
  const handleSubmit = async () => {
    if (!formValue.trim()) {
      setError("Category Specification cannot be empty");
      return;
    }

    try {
      setLoading(true);

      if (editingId) {
        await updateCategoryTypeSpecification({
          category_specification_id: editingId,
          text: formValue.trim(),
        });
      } else {
        await addCategoryTypeSpecification({
          category_id: id,
          text: formValue.trim(),
        });
      }

      showToast.success("Category Specification saved");
      setFormValue("");
      setEditingId(null);
      fetchSpecifications();
    } catch {
      showToast.error("Action failed");
    } finally {
      setLoading(false);
    }
  };


  const columns = [
    { name: "text", label: "Category Specification" },
    {
      name: "actions",
      label: "Action",
      options: {
        sort: false,
        customBodyRenderLite: (index) => {
          const row = specifications[index];
          return (
            <Box display="flex" gap={1}>
              <Tooltip title="Edit">
                <IconButton onClick={() => handleEdit(row)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton onClick={() => handleDelete(row)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    },
  ];

  return (
    <div className="py-3">
      <div
        className="d-flex align-items-center gap-2 mb-3"
        onClick={() => window.history.back()}
        style={{ cursor: "pointer" }}
      >
        <FaArrowLeftLong />
        <h3 className="mb-0 management-tile ml-2">Add / Edit Product Specification</h3>
      </div>

      <Row className="mb-3">
        <Col xs={12}>
          <FormInput
            value={formValue}
            onChange={(e) => {
              setFormValue(e.target.value);
              setError("");
            }}
            placeholder="Type category specification..."
            disabled={loading}
          />
          {error && <small className="text-danger">{error}</small>}
        </Col>

        <Col xs={3} className="mt-2">
          <Button onClick={handleSubmit} disabled={loading}>
            {editingId ? "Update Specification" : "Add Specification"}
          </Button>
        </Col>
      </Row>

      <ReusableMuiTable
        title="Category Specifications"
        data={specifications}
        columns={columns}
        loading={loading}
        options={{
          serverSide: true,
          search: true,
          searchText: searchInput,
          page: page - 1,
          count: totalRecords,
          rowsPerPage: ROWS_PER_PAGE,
          selectableRows: "none",

          onChangePage: (p) => setPage(p + 1),

          onSearchChange: handleSearchChange,

          onSearchClose: () => {
            setSearchInput("");
            setSearchSpecification("");
            setPage(1);
          },
        }}
      />
    </div>
  );
};

export default CategorySpecification;
